import { pick, uniqBy } from 'lodash';

import {
  findRegions,
  findDistrict,
  findCity,
  findLocality,
  findStreet,
  findHouseNumber,
  findBuildingNumber,
  findStructNumber,
} from 'services/FIAS_API';
import { getRequiredFn, validateNumberLength } from 'utils/reduxForm/validations';

export const TOOLTIP_TITLE = 'Подсказка';

const responseFields = {
  REGION: {
    name: 'region',
    dataLoader: findRegions,
  },
  DISTRICT: {
    name: 'district',
    dataLoader: findDistrict,
  },
  CITY: {
    name: 'city',
    dataLoader: findCity,
    additionalFields: ['district', 'region'],
  },
  LOCALITY: {
    name: 'locality',
    dataLoader: findLocality,
    additionalFields: ['district', 'region', 'city'],
  },
  STREET: {
    name: 'street',
    dataLoader: findStreet,
    additionalFields: ['city', 'locality'],
  },
  HOUSE_NUMBER: {
    name: 'houseNumber',
    dataLoader: findHouseNumber,
  },
  BUILDING_NUMBER: {
    name: 'buildingNumber',
    dataLoader: findBuildingNumber,
  },
  STRUCT_NUMBER: {
    name: 'structNumber',
    dataLoader: findStructNumber,
  },
};

const formFields = Object.freeze({
  postCode: {
    name: 'zip',
    label: 'Индекс',
    maxLength: 6,
    validate: validateNumberLength(6, undefined, true),
  },
  region: {
    name: 'subjectName',
    label: 'Субъект РФ',
    valueMinLength: 2,
    isRequired: true,
    validate: getRequiredFn(),
    optionsGetter: getOptionsGetter(responseFields.REGION),
  },
  regionCode: {
    name: 'subjectCode',
  },
  addressObjectId: {
    name: 'addressObjectId',
  },
  district: {
    name: 'region',
    label: 'Район',
    maxLength: 50,
    optionsGetter: getOptionsGetter(responseFields.DISTRICT),
  },
  city: {
    name: 'city',
    label: 'Город',
    maxLength: 50,
    optionsGetter: getOptionsGetter(responseFields.CITY),
  },
  locality: {
    name: 'locality',
    label: 'Населенный пункт',
    maxLength: 50,
    optionsGetter: getOptionsGetter(responseFields.LOCALITY),
  },
  street: {
    name: 'street',
    label: 'Улица',
    selectable: false,
    maxLength: 50,
    optionsGetter: getOptionsGetter(responseFields.STREET),
  },
  houseType: {
    name: 'houseType',
    label: 'Дом',
  },
  houseNumber: {
    name: 'building',
    selectable: false,
    valueMinLength: 1,
    label: 'Дом',
    maxLength: 20,
    optionsGetter: getOptionsGetter(responseFields.HOUSE_NUMBER, true),
  },
  buildingNumber: {
    name: 'housing',
    label: 'Корпус',
    selectable: false,
    valueMinLength: 1,
    maxLength: 20,
    optionsGetter: getOptionsGetter(responseFields.BUILDING_NUMBER, true),
  },
  structType: {
    name: 'structType',
    label: 'Строение',
  },
  structNumber: {
    name: 'structNumber',
    selectable: false,
    valueMinLength: 1,
    optionsGetter: getOptionsGetter(responseFields.STRUCT_NUMBER, true),
  },
  apartmentNumber: {
    name: 'apartment',
    label: 'Квартира',
    maxLength: 20,
  },
});

export default formFields;

export const hierarchyLevels = [
  formFields.region.name,
  formFields.district.name,
  formFields.city.name,
  formFields.locality.name,
  formFields.street.name,
  formFields.houseNumber.name,
  formFields.buildingNumber.name,
  formFields.structNumber.name,
].reverse();
export const responseFieldsMap = Object.freeze({
  [responseFields.REGION.name]: formFields.region,
  [responseFields.DISTRICT.name]: formFields.district,
  [responseFields.CITY.name]: formFields.city,
  [responseFields.LOCALITY.name]: formFields.locality,
});

export const defaultErrorText = 'Ошибка загрузки данных. Попробуйте повторить позже';

function getOptionsGetter(field, uniqueOnly) {
  return function (fieldValue, args) {
    const requestObj = field.dataLoader(fieldValue, args);
    const options = requestObj.request.then((response) => {
      if (!response.isOk) {
        throw response;
      }

      const values = response.data.map((item) => {
        let additionalFields = null;
        if (field.additionalFields) {
          additionalFields = pick(item, field.additionalFields);
        }
        return {
          value: item[field.name],
          id: item.id,
          postCode: item.postalCode ? item.postalCode : null,
          regionCode: item.regionCode || null,
          additionalFields,
          taxInspectionCode: item.ifnsFl,
        };
      });

      return uniqueOnly ? uniqBy(values, 'value') : values;
    });
    return { options, cancel: requestObj.cancel };
  };
}
